import { Checkbox, Label } from '@teamsnap/snap-ui';
import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  usePostRegEditActions,
  usePostRegEditDrawer,
} from '../../../../../hooks/postRegEditDrawer/usePostRegEditDrawer';
import { FormQuestion, FormAnswer, SelectOption } from '../types';
import { AmountRangeInput, QuantityInput } from './FormQuestionInput';

interface CheckboxComponentProps {
  option: SelectOption;
  checkedValues: string[];
  onChange: (values: string[]) => void;
  details?: string;
}

// Renders a single checkbox option and determines whether or not it should be selected
const CheckboxComponent = ({
  option,
  checkedValues,
  onChange,
}: CheckboxComponentProps) => {
  const { label, value } = option;

  return (
    <Checkbox
      value={value}
      extraProps={{
        input: {
          checked: checkedValues.includes(value),
          onChange: () => {
            onChange(
              checkedValues.includes(value)
                ? checkedValues.filter((checkedValue) => checkedValue !== value)
                : [...checkedValues, value]
            );
          },
        },
      }}
      label={label}
    />
  );
};

// Renders a checkbox OPTION (including amount/quantity inputs if applicable)
const CheckboxOption = React.memo(
  ({
    option,
    checkedValues,
    details,
    question,
    trackUpdate,
  }: {
    option: SelectOption;
    checkedValues: string[];
    details?: string;
    question: FormQuestion;
    trackUpdate: () => void;
  }) => {
    const { value, maxQuantity, amountRange } = option;
    const [showAdjustment, setShowAdjustment] = useState<boolean>(false);
    const { formErrors } = usePostRegEditDrawer();
    const { updateFormValue, updateFormDetails } = usePostRegEditActions();

    useEffect(() => {
      if (checkedValues.includes(value)) {
        setShowAdjustment(true);
      } else {
        setShowAdjustment(false);
      }
    }, [checkedValues, value]);

    let valueError: string, detailsError: string;
    if (formErrors[question.id]?.length) {
      valueError = formErrors[question.id].find(
        (formError) => !formError.details && formError.value === value
      )?.error;
      detailsError = formErrors[question.id].find(
        (formError) => formError.details === details
      )?.error;
    }

    const checkboxProps = {
      option,
      checkedValues,
      onChange: (values) => {
        updateFormValue(question.id, values);
        trackUpdate();
      },
      error: valueError,
    };

    if (!showAdjustment) {
      return <CheckboxComponent {...checkboxProps} />;
    }

    return (
      <div>
        <CheckboxComponent {...checkboxProps} />
        {maxQuantity ? (
          <QuantityInput
            maxQuantity={maxQuantity}
            value={String(details)}
            error={detailsError}
            onChange={(newQuantity) => {
              updateFormDetails(question.id, value, newQuantity);
              trackUpdate()
            }}
          />
        ) : null}
        {amountRange ? (
          <AmountRangeInput
            value={String(details)}
            error={detailsError}
            onChange={(newAmount) => {
              updateFormDetails(question.id, value, newAmount);
              trackUpdate();
            }}
          />
        ) : null}
      </div>
    );
  }
);

interface GroupCheckboxInputProps {
  question: FormQuestion;
  trackUpdate: () => void;
}

// Renders a group of checkboxes based on a single question's options
export function GroupCheckboxInput({ question, trackUpdate }: GroupCheckboxInputProps) {
  const {
    currentFormState: formState,
    formErrors,
    dirtyFields,
  } = usePostRegEditDrawer();

  const options = question.options as SelectOption[];
  const { label: questionLabel, id: name } = question;
  const answers = formState[name] as FormAnswer[];
  const checkedValues = answers.map((answer) => answer.value);

  const questionError = (formErrors[name] ?? []).map((formError) => {
    const { error, value, details } = formError;
    return !value && !details ? error : '';
  });

  return (
    <div className="sui-pb-2">
      <Label htmlFor={name}>
        {`${questionLabel} ${dirtyFields.includes(name) ? '(Edited)' : ''}`}
      </Label>
      {options.map((option, index) => (
        <CheckboxOption
          key={`${questionLabel}-${option.value}-${index}`}
          option={option}
          checkedValues={checkedValues}
          question={question}
          details={
            answers.find((answer) => answer.value === option.value)?.details
          }
          trackUpdate={trackUpdate}
        />
      ))}
      {questionError ? (
        <span className={'sui-text-negative-text sui-caption'}>
          {questionError}
        </span>
      ) : null}
    </div>
  );
}
